import React, { useEffect, useState, Component } from 'react';
import { MapPin, Save, CheckCircle, AlertCircle, ArrowLeft, RefreshCw, Image as ImageIcon, Camera } from 'lucide-react';
interface PendingPhoto {
  id: string;
  telefono: string;
  image_url: string;
  proyecto: string;
  plano: string;
  x: number | null;
  y: number | null;
}
interface Plano {
  id: string;
  nombre: string;
  image_url: string;
}
interface PinCoord {
  x: number;
  y: number;
}
const WEBHOOK_PENDING = 'https://hook.us2.make.com/7q7rxl9c89whmn99vu8rl2ykl5km89yh';
const WEBHOOK_PLANOS = 'https://hook.us2.make.com/vuw0tofkfnyis1jd31bdrb98laopr907';
const WEBHOOK_SAVE = 'https://hook.us2.make.com/dgr6qsvlp5htc1tp31xge9qe0brbx3o9';
export function PinFotos() {
  // URL param
  const [waId, setWaId] = useState('');
  // Pending photos
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  // Selected photo for pinning
  const [activePhoto, setActivePhoto] = useState<PendingPhoto | null>(null);
  // Planos
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [loadingPlanos, setLoadingPlanos] = useState(false);
  const [selectedPlanoId, setSelectedPlanoId] = useState('');
  // Pin
  const [pin, setPin] = useState<PinCoord | null>(null);
  // Save
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<'idle' | 'success' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');
  // Read wa_id and pin_id from URL on mount
  const [pinId] = useState(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get('pin_id') || '';
  });
  useEffect(() => {
    const url = new URL(window.location.href);
    const id = url.searchParams.get('wa_id');
    if (id) setWaId(id);
  }, []);
  // Fetch photos + planos on mount
  useEffect(() => {
    if (!waId) return;
    fetchPhotos();
    fetchPlanos();
  }, [waId]);
  // Deep link: auto-select photo matching pin_id after photos load
  useEffect(() => {
    if (!pinId || photos.length === 0) return;
    if (activePhoto) return;
    console.log('PIN_ID:', pinId);
    console.log('PHOTOS:', photos.length, photos.map(p => p.id));
    // If backend returned exactly 1 photo for this pin_id, open it directly
    if (photos.length === 1) {
      console.log('MATCH (single):', photos[0]);
      setActivePhoto(photos[0]);
      return;
    }
    // Otherwise find by id
    const target = photos.find(p => p.id === pinId);
    console.log('MATCH (find):', target);
    if (target) {
      setActivePhoto(target);
    }
  }, [photos]);
  const fetchPhotos = async () => {
    setLoadingPhotos(true);
    try {
      const fetchUrl = `${WEBHOOK_PENDING}?wa_id=${encodeURIComponent(waId)}&pin_id=${encodeURIComponent(pinId)}`;
      const res = await fetch(fetchUrl, {
        method: 'GET'
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.data || [];
        setPhotos(list);
      }
    } catch (err) {
      console.error('Error fetching photos:', err);
    } finally {
      setLoadingPhotos(false);
    }
  };
  const fetchPlanos = async () => {
    setLoadingPlanos(true);
    try {
      const res = await fetch(WEBHOOK_PLANOS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          wa_id: waId
        })
      });
      if (res.ok) {
        const data = await res.json();
        const list: Plano[] = Array.isArray(data) ? data : data?.data || [];
        setPlanos(list);
        if (list.length === 1) {
          setSelectedPlanoId(list[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching planos:', err);
    } finally {
      setLoadingPlanos(false);
    }
  };
  const handlePlanoClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setPin({
      x,
      y
    });
    setSaveResult('idle');
    setSaveError('');
  };
  const handleSave = async () => {
    const plano = planos.find(p => p.id === selectedPlanoId);
    if (!activePhoto || !plano || !pin || planos.length === 0) {
      setSaveResult('error');
      setSaveError('No tienes planos disponibles');
      return;
    }
    setSaving(true);
    setSaveResult('idle');
    setSaveError('');
    try {
      const res = await fetch(WEBHOOK_SAVE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          record_id: activePhoto.id,
          plano: plano.nombre,
          x: Number(pin.x),
          y: Number(pin.y),
          estado: 'completo'
        })
      });
      const data = await res.json().catch(() => null);
      console.log('Save response:', data);
      if (res.ok && data?.ok === true) {
        setSaveResult('success');
        setPin(null);
        setPhotos(prev => prev.filter(p => p.id !== activePhoto.id));
        fetchPhotos();
        setTimeout(() => {
          setActivePhoto(null);
          setSaveResult('idle');
        }, 1500);
      } else {
        setSaveResult('error');
        setSaveError('Error en servidor');
      }
    } catch (err) {
      console.error('Save error:', err);
      setSaveResult('error');
      setSaveError('Error de conexión');
    } finally {
      setSaving(false);
    }
  };
  const goBack = () => {
    setActivePhoto(null);
    setPin(null);
    setSaveResult('idle');
    setSaveError('');
  };
  const selectedPlano = planos.find(p => p.id === selectedPlanoId);
  if (!waId) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-522">
        <p className="text-sm text-gray-500" data-id="element-523">
          No se detectó teléfono en la URL
        </p>
      </div>;
  }
  // ─── MAIN APP ───
  return <div className="min-h-screen bg-gray-50 pb-24 sm:pb-12" data-id="element-524">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-20" data-id="element-525">
        <div className="max-w-5xl mx-auto px-4 py-4" data-id="element-526">
          <div className="flex items-center justify-between" data-id="element-527">
            <div className="flex items-center" data-id="element-528">
              <MapPin className="w-6 h-6 text-blue-600 mr-2" data-id="element-529" />
              <div data-id="element-530">
                <h1 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-531">
                  Ubicar fotos
                </h1>
                <p className="text-xs text-gray-500 hidden sm:block" data-id="element-532">
                  Selecciona una foto pendiente y márcala en el plano
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-533">
              {!activePhoto ? <button onClick={fetchPhotos} className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors" data-id="element-534">
                  <RefreshCw className={`w-4 h-4 mr-1.5 ${loadingPhotos ? 'animate-spin' : ''}`} data-id="element-535" />
                  <span className="hidden sm:inline" data-id="element-536">Actualizar</span>
                </button> : <button onClick={goBack} className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors" data-id="element-537">
                  <ArrowLeft className="w-4 h-4 mr-1.5" data-id="element-538" /> Volver
                </button>}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1 sm:hidden" data-id="element-539">
            Selecciona una foto pendiente y márcala en el plano
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 sm:py-8" data-id="element-540">
        {!activePhoto /* ─── LIST VIEW ─── */ ? <>
            {loadingPhotos ? <div className="text-center py-20" data-id="element-541">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" data-id="element-542" />
                <p className="mt-3 text-sm text-gray-500" data-id="element-543">
                  Cargando fotos pendientes...
                </p>
              </div> : photos.length === 0 ? <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm" data-id="element-544">
                <ImageIcon className="mx-auto h-12 w-12 text-gray-300" data-id="element-545" />
                <h3 className="mt-4 text-base font-medium text-gray-900" data-id="element-546">
                  No hay fotos pendientes
                </h3>
                <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-547">
                  Cuando alguien mande una foto por WhatsApp, aparecerá aquí.
                </p>
              </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-id="element-548">
                {photos.map(photo => <div key={photo.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col transition-shadow hover:shadow-md" data-id="element-549">
                    <div className="aspect-[4/3] w-full bg-gray-100 relative" data-id="element-550">
                      {photo.image_url ? <img src={photo.image_url} alt="Foto pendiente" className="w-full h-full object-cover" data-id="element-551" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-552">
                          <Camera className="w-10 h-10 text-gray-300" data-id="element-553" />
                        </div>}
                    </div>
                    <div className="p-5 flex flex-col flex-grow" data-id="element-554">
                      <div className="mb-4" data-id="element-555">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-556">
                          {photo.telefono}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5" data-id="element-557">
                          ID: {photo.id.substring(0, 8)}
                        </p>
                      </div>
                      <div className="mt-auto" data-id="element-558">
                        <button onClick={() => {
                  setActivePhoto(photo);
                  setPin(null);
                  setSaveResult('idle');
                  setSaveError('');
                  setSelectedPlanoId('');
                }} className="w-full flex justify-center items-center py-2.5 px-4 rounded-xl text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors" data-id="element-559">
                          <MapPin className="w-4 h-4 mr-2" data-id="element-560" />
                          Ubicar foto
                        </button>
                      </div>
                    </div>
                  </div>)}
              </div>}
          </> /* ─── PIN MODE ─── */ : <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6 lg:gap-8 items-start" data-id="element-561">
            {/* Left Column: Photo Preview */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-562">
              <div className="aspect-[4/3] lg:aspect-auto lg:h-80 w-full bg-gray-100" data-id="element-563">
                {activePhoto.image_url ? <img src={activePhoto.image_url} alt="Foto a ubicar" className="w-full h-full object-cover" data-id="element-564" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-565">
                    <ImageIcon className="w-12 h-12 text-gray-300" data-id="element-566" />
                  </div>}
              </div>
              <div className="p-5 bg-gray-50/50 border-t border-gray-100" data-id="element-567">
                <p className="text-sm text-gray-600" data-id="element-568">
                  <span className="font-medium text-gray-900 block mb-0.5" data-id="element-569">
                    Teléfono
                  </span>
                  {activePhoto.telefono}
                </p>
                <p className="text-xs text-gray-400 mt-2" data-id="element-570">
                  ID: {activePhoto.id}
                </p>
              </div>
            </div>

            {/* Right Column: Plan & Pinning */}
            <div className="space-y-6" data-id="element-571">
              {/* Plan Selector */}
              <section className="bg-white p-5 sm:p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-572">
                <div className="mb-4" data-id="element-573">
                  <label className="block text-base font-semibold text-gray-900" data-id="element-574">
                    Plano
                  </label>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-575">
                    Elige el plano donde fue tomada la foto.
                  </p>
                </div>

                {loadingPlanos ? <div className="flex items-center text-sm text-gray-500 py-2" data-id="element-576">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2" data-id="element-577" />
                    Cargando planos...
                  </div> : planos.length > 0 ? <select value={selectedPlanoId} onChange={e => {
              setSelectedPlanoId(e.target.value);
              setPin(null);
            }} className="block w-full pl-3 pr-10 py-3 text-base border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-xl bg-gray-50" data-id="element-578">
                    {planos.map(p => <option key={p.id} value={p.id} data-id="element-579">
                        {p.nombre}
                      </option>)}
                  </select> : <div className="py-4 px-4 bg-gray-50 rounded-xl border border-gray-200 text-sm text-gray-500 flex items-center" data-id="element-580">
                    <AlertCircle className="w-4 h-4 mr-2 text-gray-400" data-id="element-581" />
                    {waId ? 'No se encontraron planos.' : 'No se detectó teléfono en la URL.'}
                  </div>}
              </section>

              {/* Plan Interaction */}
              {selectedPlano && <section className="bg-white p-5 sm:p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-582">
                  <div className="flex items-center justify-between mb-4" data-id="element-583">
                    <p className="text-sm font-medium text-gray-900 flex items-center" data-id="element-584">
                      <MapPin className="w-4 h-4 mr-1.5 text-blue-600" data-id="element-585" />
                      Toca el punto exacto en el plano
                    </p>
                    {pin && <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-md flex items-center" data-id="element-586">
                        <CheckCircle className="w-3 h-3 mr-1" data-id="element-587" /> Pin
                        seleccionado
                      </span>}
                  </div>

                  <div className="relative border border-gray-200 rounded-xl overflow-hidden bg-gray-100" data-id="element-588">
                    <img src={selectedPlano.image_url} alt={selectedPlano.nombre} onClick={e => {
                handlePlanoClick(e);
              }} className="w-full h-[500px] object-contain cursor-crosshair z-10 relative" data-id="element-589" />
                    {pin && <>
                        <div className="absolute w-6 h-6 bg-blue-600 rounded-full border-[3px] border-white shadow-md -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10" style={{
                  left: `${pin.x * 100}%`,
                  top: `${pin.y * 100}%`
                }} data-id="element-590" />
                        <div className="absolute w-10 h-10 bg-blue-600/30 rounded-full animate-ping -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{
                  left: `${pin.x * 100}%`,
                  top: `${pin.y * 100}%`
                }} data-id="element-591" />
                      </>}
                  </div>
                </section>}

              {/* Desktop Save Button (hidden on mobile, sticky on mobile) */}
              <div className="hidden sm:block" data-id="element-592">
                <SaveButton handleSave={handleSave} saving={saving} saveResult={saveResult} saveError={saveError} disabled={saving || saveResult === 'success' || !pin || !selectedPlano} data-id="element-593" />
              </div>
            </div>
          </div>}
      </main>

      {/* Mobile Sticky Save Button */}
      {activePhoto && <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20" data-id="element-594">
          <SaveButton handleSave={handleSave} saving={saving} saveResult={saveResult} saveError={saveError} disabled={saving || saveResult === 'success' || !pin || !selectedPlano} data-id="element-595" />
        </div>}
    </div>;
}
// Helper component for the save button to reuse between desktop and mobile sticky
function SaveButton({
  handleSave,
  saving,
  saveResult,
  saveError,
  disabled
}: any) {
  return <div className="space-y-3" data-id="element-596">
      <button onClick={handleSave} disabled={disabled} className="w-full flex justify-center items-center py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-597">
        {saving ? <span className="flex items-center" data-id="element-598">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" data-id="element-599">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-600" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-601" />
            </svg>
            Guardando...
          </span> : <span className="flex items-center" data-id="element-602">
            <Save className="w-4 h-4 mr-2" data-id="element-603" /> Guardar ubicación
          </span>}
      </button>

      {saveResult === 'success' && <div className="rounded-xl bg-green-50 p-3.5 border border-green-200 flex items-start" data-id="element-604">
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" data-id="element-605" />
          <p className="ml-3 text-sm font-medium text-green-800" data-id="element-606">
            Ubicación guardada. Volviendo...
          </p>
        </div>}

      {saveResult === 'error' && <div className="rounded-xl bg-red-50 p-3.5 border border-red-200 flex items-start" data-id="element-607">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" data-id="element-608" />
          <p className="ml-3 text-sm font-medium text-red-800" data-id="element-609">{saveError}</p>
        </div>}
    </div>;
}